import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { safeFormat } from "@/lib/dateUtils";
import { buildDecideTodayRecommendations, type OperationalRecommendation } from "@/lib/decideToday";
import { useAuth } from "@/_core/hooks/useAuth";
import StaffDashboard from "./StaffDashboard";
import RoleDashboard from "./RoleDashboard";
import { DesktopOperationDesk } from "@/components/admin/DesktopOperationDesk";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import {
  CalendarDays, TrendingUp, Users, DollarSign,
  CheckCircle, XCircle, AlertCircle, BarChart3, Loader2, Package, ClipboardList, ChevronRight, CreditCard, FileText, UserRoundCheck, Sparkles, ThumbsUp,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from "recharts";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";

const STATUS_COLORS_MAP: Record<string, string> = {
  pending: "#EAB308", confirmed: "#22C55E", cancelled: "#EF4444",
  completed: "#3B82F6", no_show: "#6B7280",
};
const STATUS_LABELS: Record<string, string> = {
  pending: "Pendente", confirmed: "Confirmado", cancelled: "Cancelado",
  completed: "Concluído", no_show: "Não compareceu",
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [period, setPeriod] = useState<"week" | "month">("month");

  // Verificar o staffRole do usuário logado
  const { data: staffCtx, isLoading: isLoadingStaffContext } = trpc.staff.myContext.useQuery();
  const staffRole = staffCtx?.staffRole;
  const isProfessionalDashboard = ["funcionario", "professional", "profissional"].includes(staffRole ?? "");
  const isRoleDashboard = staffRole === "recepcionista" || staffRole === "caixa";
  const isAdminDashboard = !isLoadingStaffContext
    && !isProfessionalDashboard
    && !isRoleDashboard
    && (staffRole === "admin" || (!staffCtx && user?.role === "admin"));

  const dateRange = useMemo(() => {
    const now = new Date();
    if (period === "week") {
      return { startDate: startOfWeek(now, { weekStartsOn: 1 }), endDate: endOfWeek(now, { weekStartsOn: 1 }) };
    }
    return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
  }, [period]);
  const todayRange = useMemo(() => {
    const now = new Date();
    return { startDate: startOfDay(now), endDate: endOfDay(now) };
  }, []);

  const { data: analytics, isLoading } = trpc.analytics.getSummary.useQuery(dateRange, { enabled: isAdminDashboard });
  const { data: mySalon } = trpc.salon.getMySalon.useQuery(undefined, { enabled: isAdminDashboard });
  // Stable query inputs — objetos literais inline causam re-fetches infinitos
  const lowStockInput = useMemo(() => ({ lowStock: true as const }), []);
  const { data: lowStockProducts } = trpc.products.list.useQuery(lowStockInput, { enabled: isAdminDashboard });
  const { data: queueItems } = (trpc as any).queue.list.useQuery(undefined, { enabled: isAdminDashboard, refetchInterval: 30000 });
  const { data: experienceSummary } = trpc.experience.summary.useQuery(
    { days: 7 },
    { enabled: isAdminDashboard }
  );
  const { data: unpaidAppointments } = trpc.checkout.listUnpaid.useQuery(
    { period: "today" },
    { enabled: isAdminDashboard }
  );
  const { data: staffMembers } = trpc.staff.list.useQuery(undefined, { enabled: isAdminDashboard });
  const { data: pendingCancellationData } = (trpc as any).cancellationRequests.countPending.useQuery(undefined, {
    enabled: isAdminDashboard,
    refetchInterval: 60000,
  });
  const intelligenceQuery = (trpc as any).intelligence.executive.useQuery(undefined, { enabled: isAdminDashboard });
  const recommendationDecisionsQuery = (trpc as any).intelligence.recommendationDecisions.useQuery(undefined, { enabled: isAdminDashboard });
  const reviewRecommendationMutation = (trpc as any).intelligence.reviewRecommendation.useMutation({
    onSuccess: () => { void recommendationDecisionsQuery.refetch(); },
  });

  const salonId = mySalon?.id;
  const dashProfsInput = useMemo(() => ({ salonId: salonId! }), [salonId]);
  const dashServicesInput = useMemo(() => ({ salonId: salonId! }), [salonId]);
  const { data: recentAppts } = trpc.appointments.getByDateRange.useQuery(dateRange, { enabled: isAdminDashboard });
  const { data: todayAppointments } = trpc.appointments.getByDateRange.useQuery(todayRange, { enabled: isAdminDashboard });
  const { data: professionals } = trpc.professionals.list.useQuery(
    dashProfsInput,
    { enabled: !!salonId }
  );
  const { data: services } = trpc.services.list.useQuery(
    dashServicesInput,
    { enabled: !!salonId }
  );

  const getProfName = (id: number) => professionals?.find((p) => p.id === id)?.name ?? "—";
  const getServiceName = (id: number) => services?.find((s: { id: number; name: string }) => s.id === id)?.name ?? "—";

  const pieData = analytics?.statusCounts?.map((s) => ({
    name: STATUS_LABELS[s.status] ?? s.status,
    value: Number(s.count),
    color: STATUS_COLORS_MAP[s.status] ?? "#8B6914",
  })) ?? [];

  const confirmedCount = analytics?.statusCounts?.find((s) => s.status === "confirmed")?.count ?? 0;
  const pendingCount = analytics?.statusCounts?.find((s) => s.status === "pending")?.count ?? 0;
  const cancelledCount = analytics?.statusCounts?.find((s) => s.status === "cancelled")?.count ?? 0;
  const completedCount = analytics?.statusCounts?.find((s) => s.status === "completed")?.count ?? 0;
  const totalAppts = pieData.reduce((acc, d) => acc + d.value, 0);
  const pendingPaymentCount = unpaidAppointments?.length ?? 0;
  const pendingCancellationCount = pendingCancellationData?.count ?? 0;
  const todayPendingCount = (todayAppointments ?? []).filter((appointment) => appointment.status === "pending").length;
  const todayConfirmedCount = (todayAppointments ?? []).filter((appointment) => appointment.status === "confirmed").length;
  const todayCompletedCount = (todayAppointments ?? []).filter((appointment) => appointment.status === "completed").length;
  const waitingQueueCount = (queueItems ?? []).filter((item: any) => item.status === "waiting").length;
  const preparingQueueCount = (queueItems ?? []).filter((item: any) => item.status === "preparing").length;
  const calledQueueCount = (queueItems ?? []).filter((item: any) => item.status === "called").length;
  const overduePreparationCount = (queueItems ?? []).filter((item: any) => {
    if (item.status !== "preparing" || !item.preparationStartedAt) return false;
    return Date.now() - new Date(item.preparationStartedAt).getTime() >= 25 * 60 * 1000;
  }).length;
  const queueActionCount = waitingQueueCount + preparingQueueCount + calledQueueCount;
  const unlinkedStaffCount = (staffMembers ?? []).filter((member: any) => member.staffRole !== "admin" && !member.professionalId).length;
  const contractDataMissing = !(mySalon as any)?.cnpj || !(mySalon as any)?.legalResponsible;
  const executiveRecommendations = Array.isArray(intelligenceQuery.data?.recommendations)
    ? intelligenceQuery.data.recommendations as OperationalRecommendation[]
    : [];
  const recommendationDecisions = Array.isArray(recommendationDecisionsQuery.data)
    ? recommendationDecisionsQuery.data
    : [];
  const decideTodayRecommendations = buildDecideTodayRecommendations(executiveRecommendations, recommendationDecisions).slice(0, 3);

  const reviewRecommendation = (recommendation: OperationalRecommendation, decision: "approved" | "dismissed") => {
    reviewRecommendationMutation.mutate({
      recommendationKey: recommendation.id,
      recommendationTitle: recommendation.title,
      actionPath: recommendation.actionPath,
      decision,
    });
  };

  const pendingItems = [
    pendingPaymentCount > 0 ? { key: "payments", count: pendingPaymentCount, title: `${pendingPaymentCount} pagamento${pendingPaymentCount > 1 ? "s" : ""} aguardando`, description: "Receba os atendimentos pendentes no caixa.", path: "/checkout", icon: CreditCard, tone: "text-emerald-700 bg-emerald-50 border-emerald-200" } : null,
    todayPendingCount > 0 ? { key: "appointments", count: todayPendingCount, title: `${todayPendingCount} agendamento${todayPendingCount > 1 ? "s" : ""} a confirmar hoje`, description: "Revise os próximos horários da agenda antes do atendimento.", path: "/weekly-calendar", icon: CalendarDays, tone: "text-amber-700 bg-amber-50 border-amber-200" } : null,
    queueActionCount > 0 ? { key: "queue", count: queueActionCount, title: `${queueActionCount} cliente${queueActionCount > 1 ? "s" : ""} na fila de chamada`, description: overduePreparationCount > 0 ? `${waitingQueueCount} aguardando · ${preparingQueueCount} em preparação · Atenção: ${overduePreparationCount} montagem(ns) acima de 25 min.` : `${waitingQueueCount} aguardando · ${preparingQueueCount} em preparação · ${calledQueueCount} chamada(s).`, path: "/painel-chamada", icon: Users, tone: overduePreparationCount > 0 ? "text-rose-700 bg-rose-50 border-rose-200" : "text-sky-700 bg-sky-50 border-sky-200" } : null,
    pendingCancellationCount > 0 ? { key: "cancellations", count: pendingCancellationCount, title: `${pendingCancellationCount} cancelamento${pendingCancellationCount > 1 ? "s" : ""} pendente${pendingCancellationCount > 1 ? "s" : ""}`, description: "Avalie as solicitações enviadas pelos clientes.", path: "/cancellation-requests", icon: AlertCircle, tone: "text-rose-700 bg-rose-50 border-rose-200" } : null,
    (lowStockProducts?.length ?? 0) > 0 ? { key: "stock", count: lowStockProducts!.length, title: `${lowStockProducts!.length} produto${lowStockProducts!.length > 1 ? "s" : ""} com estoque baixo`, description: "Reponha itens antes que o estoque acabe.", path: "/products", icon: Package, tone: "text-orange-700 bg-orange-50 border-orange-200" } : null,
    unlinkedStaffCount > 0 ? { key: "staff", count: unlinkedStaffCount, title: `${unlinkedStaffCount} membro${unlinkedStaffCount > 1 ? "s" : ""} sem vínculo profissional`, description: "Vincule a equipe para agenda, ponto e comissões funcionarem.", path: "/staff", icon: UserRoundCheck, tone: "text-blue-700 bg-blue-50 border-blue-200" } : null,
    contractDataMissing ? { key: "contract", count: 1, title: "Dados do contrato incompletos", description: "Informe CNPJ/CPF e responsável legal nas configurações.", path: "/settings", icon: FileText, tone: "text-violet-700 bg-violet-50 border-violet-200" } : null,
  ].filter(Boolean) as Array<{ key: string; count: number; title: string; description: string; path: string; icon: typeof ClipboardList; tone: string }>;
  const nextOperationalAction = pendingItems[0] ?? {
    key: "agenda",
    count: 0,
    title: "Acompanhar a agenda de hoje",
    description: "Revise os próximos atendimentos e mantenha a operação organizada.",
    path: "/weekly-calendar",
    icon: CalendarDays,
    tone: "text-primary bg-primary/10 border-primary/20",
  };
  const deskWidgets = [
    { id: "next-action" as const, title: "Próximo passo", value: nextOperationalAction.count > 0 ? String(nextOperationalAction.count) : "Agora", description: nextOperationalAction.title, icon: nextOperationalAction.icon, tone: nextOperationalAction.tone, onClick: () => navigate(nextOperationalAction.path) },
    { id: "pending" as const, title: "Pendências", value: String(pendingItems.length), description: pendingItems.length > 0 ? "Itens esperando ação hoje" : "Nenhuma pendência crítica", icon: ClipboardList, tone: "bg-amber-100 text-amber-700", onClick: () => document.getElementById("pending-items")?.scrollIntoView({ behavior: "smooth", block: "start" }) },
    { id: "recommendations" as const, title: "Decida hoje", value: String(decideTodayRecommendations.length), description: "Recomendações aguardando revisão", icon: Sparkles, tone: "bg-violet-100 text-violet-700", onClick: () => document.getElementById("decide-today")?.scrollIntoView({ behavior: "smooth", block: "start" }) },
    { id: "stock" as const, title: "Estoque", value: String(lowStockProducts?.length ?? 0), description: (lowStockProducts?.length ?? 0) > 0 ? "Produtos abaixo do mínimo" : "Sem alerta de reposição", icon: Package, tone: "bg-orange-100 text-orange-700", onClick: () => navigate("/products") },
  ];

  const stats = [
    { title: "Receita Total", value: `R$ ${Number(analytics?.revenue?.total ?? 0).toFixed(2).replace(".", ",")}`, icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50" },
    { title: "Total de Agendamentos", value: String(totalAppts), icon: CalendarDays, color: "text-blue-600", bg: "bg-blue-50" },
    { title: "Confirmados", value: String(confirmedCount), icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
    { title: "Pendentes", value: String(pendingCount), icon: AlertCircle, color: "text-yellow-600", bg: "bg-yellow-50" },
    { title: "Concluídos", value: String(completedCount), icon: TrendingUp, color: "text-purple-600", bg: "bg-purple-50" },
    { title: "Cancelados", value: String(cancelledCount), icon: XCircle, color: "text-red-600", bg: "bg-red-50" },
  ];

  // Funcionários veem o Dashboard personalizado com seus agendamentos do dia
  if (isLoadingStaffContext) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (isProfessionalDashboard) {
    return <StaffDashboard />;
  }

  if (isRoleDashboard) {
    return <RoleDashboard role={staffRole} />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Minha operação agora */}
      <section className="rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-amber-50/60 p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <Badge variant="outline" className="mb-2 border-primary/25 bg-background/80 text-primary">Gestão do salão</Badge>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">Minha operação agora</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              {mySalon?.name ?? "Seu salão"} — concentre-se primeiro no que precisa de atenção para o turno seguir sem atrasos.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant={period === "week" ? "default" : "outline"} size="sm" onClick={() => setPeriod("week")} className={period === "week" ? "bg-amber-600 hover:bg-amber-700" : ""}>
              Semana
            </Button>
            <Button variant={period === "month" ? "default" : "outline"} size="sm" onClick={() => setPeriod("month")} className={period === "month" ? "bg-amber-600 hover:bg-amber-700" : ""}>
              Mês
            </Button>
          </div>
        </div>
        <button type="button" onClick={() => navigate(nextOperationalAction.path)} className="mt-4 flex w-full items-center gap-3 rounded-xl border border-border bg-background/85 p-3 text-left transition-colors hover:border-primary/35 hover:bg-accent/30 sm:max-w-2xl">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${nextOperationalAction.tone}`}><nextOperationalAction.icon className="h-5 w-5" /></span>
          <span className="min-w-0 flex-1"><span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">Próximo passo recomendado</span><span className="block text-sm font-semibold">{nextOperationalAction.title}</span><span className="block truncate text-xs text-muted-foreground">{nextOperationalAction.description}</span></span>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </section>

      <Card id="decide-today" className="border-violet-200 bg-gradient-to-br from-violet-50/80 via-background to-background shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-base font-semibold">
            <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" />Decida hoje</span>
            <Badge variant="outline" className="border-violet-200 bg-background text-violet-700">Recomendações com aprovação humana</Badge>
          </CardTitle>
          <p className="text-xs font-normal text-muted-foreground">Analise a sugestão, aprove a prioridade e só então avance para a ação. Nenhum contato ou alteração é executado automaticamente.</p>
        </CardHeader>
        <CardContent>
          {intelligenceQuery.isLoading ? (
            <div className="grid gap-2 md:grid-cols-3">{Array.from({ length: 3 }).map((_, index) => <div key={index} className="h-28 animate-pulse rounded-xl border bg-muted/30" />)}</div>
          ) : decideTodayRecommendations.length === 0 ? (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4"><CheckCircle className="h-5 w-5 shrink-0 text-emerald-600" /><div><p className="text-sm font-medium text-emerald-800">Nenhuma nova decisão prioritária.</p><p className="text-xs text-emerald-700">Os indicadores estão estáveis ou as recomendações atuais já foram dispensadas.</p></div></div>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              {decideTodayRecommendations.map((recommendation) => {
                const isApproved = recommendation.decision === "approved";
                return <div key={recommendation.id} className="flex min-w-0 flex-col rounded-xl border bg-card p-3">
                  <div className="flex items-start justify-between gap-2"><Badge variant="outline" className={recommendation.priority === "high" ? "border-rose-200 bg-rose-50 text-rose-700" : recommendation.priority === "medium" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}>{recommendation.priority === "high" ? "Alta prioridade" : recommendation.priority === "medium" ? "Atenção" : "Acompanhar"}</Badge>{isApproved && <Badge className="bg-violet-600 text-white"><ThumbsUp className="mr-1 h-3 w-3" />Aprovada</Badge>}</div>
                  <p className="mt-3 text-sm font-semibold">{recommendation.title}</p><p className="mt-1 flex-1 text-xs leading-relaxed text-muted-foreground">{recommendation.description}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {isApproved ? <Button size="sm" className="h-8" onClick={() => navigate(recommendation.actionPath)}>Abrir ação</Button> : <><Button size="sm" className="h-8" onClick={() => reviewRecommendation(recommendation, "approved")} disabled={reviewRecommendationMutation.isPending}>Aprovar</Button><Button size="sm" variant="outline" className="h-8" onClick={() => reviewRecommendation(recommendation, "dismissed")} disabled={reviewRecommendationMutation.isPending}>Dispensar</Button></>}
                  </div>
                </div>;
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trial warning */}
      {mySalon?.status === "trial" && mySalon.trialEndsAt && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-amber-800 font-medium text-sm">Período de teste ativo</p>
            <p className="text-amber-700 text-xs">Seu trial termina em {safeFormat(mySalon.trialEndsAt, "dd/MM/yyyy")}. Assine um plano para continuar.</p>
          </div>
          <Button size="sm" className="ml-auto bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate("~/pricing")}>Ver Planos</Button>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {stats.map((stat) => (
          <Card key={stat.title} className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center mb-3`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-1">{stat.title}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pendências de hoje */}
      <Card id="pending-items" className="border-primary/20 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center justify-between gap-3">
            <span className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-primary" />Pendências de hoje</span>
            <Badge variant={pendingItems.length > 0 ? "default" : "secondary"} className={pendingItems.length > 0 ? "bg-primary text-primary-foreground" : ""}>{pendingItems.length} {pendingItems.length === 1 ? "item" : "itens"}</Badge>
          </CardTitle>
          <p className="pt-1 text-xs font-normal text-muted-foreground">{todayPendingCount} a confirmar · {todayConfirmedCount} confirmados · {todayCompletedCount} concluídos · {queueActionCount} na fila hoje.{experienceSummary ? ` ${experienceSummary.total} atalho${experienceSummary.total === 1 ? "" : "s"} usado${experienceSummary.total === 1 ? "" : "s"} nos últimos 7 dias.` : ""}</p>
        </CardHeader>
        <CardContent>
          {pendingItems.length === 0 ? (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <CheckCircle className="h-5 w-5 shrink-0 text-emerald-600" />
              <div><p className="text-sm font-medium text-emerald-800">Tudo em ordem para hoje.</p><p className="text-xs text-emerald-700">Não há pendências operacionais que exijam sua atenção neste turno.</p></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {pendingItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button key={item.key} type="button" onClick={() => navigate(item.path)} className="group flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:border-primary/35 hover:bg-accent/30">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${item.tone}`}><Icon className="h-4 w-4" /></span>
                    <span className="min-w-0 flex-1"><span className="block text-sm font-medium">{item.title}</span><span className="block truncate text-xs text-muted-foreground">{item.description}</span></span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <DesktopOperationDesk salonId={salonId} widgets={deskWidgets} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-amber-600" />
              Distribuição por Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [`${value} agendamentos`]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
                Nenhum agendamento no período
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {pieData.map((d) => (
                <div key={d.name} className="flex items-center gap-1 text-xs text-gray-600">
                  <div className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                  {d.name} ({d.value})
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
              <Button variant="outline" className="w-full justify-start gap-2 h-10" onClick={() => navigate("/appointments")}>
                <CalendarDays className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="truncate">Agendamentos</span>
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 h-10" onClick={() => navigate("/professionals")}>
                <Users className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="truncate">Profissionais</span>
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 h-10" onClick={() => navigate("/services")}>
                <BarChart3 className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="truncate">Serviços</span>
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 h-10" onClick={() => navigate("/reviews")}>
                <TrendingUp className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="truncate">Avaliações</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low stock alert card */}
      {lowStockProducts && lowStockProducts.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-orange-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Package className="h-4 w-4 text-orange-500" />
                <span className="text-orange-700">Estoque Baixo</span>
                <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-xs">{lowStockProducts.length} produto{lowStockProducts.length > 1 ? 's' : ''}</Badge>
              </span>
              <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700" onClick={() => navigate("/products")}>Gerenciar Estoque</Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {lowStockProducts.slice(0, 5).map((product) => (
                <div key={product.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.name} className="w-8 h-8 rounded-md object-cover shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-md bg-orange-100 flex items-center justify-center shrink-0">
                        <Package className="h-4 w-4 text-orange-500" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{product.name}</p>
                      <p className="text-xs text-gray-500 truncate">{product.category ?? 'Sem categoria'} · Mín: {product.stockMin ?? 0} {product.unit ?? 'un'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="text-xs border-orange-300 text-orange-700 bg-orange-50">
                      {product.stockQty ?? 0} {product.unit ?? 'un'}
                    </Badge>
                    {Number(product.stockQty ?? 0) === 0 && (
                      <Badge className="text-xs bg-red-100 text-red-700 border-red-300">Esgotado</Badge>
                    )}
                  </div>
                </div>
              ))}
              {lowStockProducts.length > 5 && (
                <p className="text-xs text-gray-400 text-center pt-1">+{lowStockProducts.length - 5} outros produtos com estoque baixo</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent appointments */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center justify-between">
            <span className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-amber-600" />
              Agendamentos Recentes
            </span>
            <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700" onClick={() => navigate("/appointments")}>Ver todos</Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentAppts && recentAppts.length > 0 ? (
            <div className="space-y-2">
              {recentAppts.slice(0, 8).map((appt) => (
                <div key={appt.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold shrink-0">
                      {appt.clientId}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{getServiceName(appt.serviceId)}</p>
                      <p className="text-xs text-gray-500 truncate">{getProfName(appt.professionalId)} · {safeFormat(appt.startTime, "dd/MM HH:mm")}</p>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className="text-xs shrink-0"
                    style={{ borderColor: STATUS_COLORS_MAP[appt.status], color: STATUS_COLORS_MAP[appt.status] }}
                  >
                    {STATUS_LABELS[appt.status] ?? appt.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-400 text-sm py-8">Nenhum agendamento no período selecionado.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
